/* HUENIT LAB 2.0 — Admin: 언어 설정 관리.
   CMS 전용 localStorage(plan §7.2). 예제·가이드 입력의 언어별 탭 소스. */
import { useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, useDragSort, useToast } from '../../components/index.js';
import { useLocal, move, LANG_SEED } from '../../data/store.js';

export function LanguagesAdmin() {
  const toast = useToast();
  const [langs, setLangs] = useLocal('adm.langs', LANG_SEED);

  const [editing, setEditing] = useState(null); // {code,label} or 'new'
  const [draft, setDraft] = useState({ code: '', label: '' });

  const openNew = () => { setDraft({ code: '', label: '' }); setEditing('new'); };
  const openEdit = (l) => { setDraft({ code: l.code, label: l.label }); setEditing(l.code); };

  const save = () => {
    const code = draft.code.trim().toLowerCase();
    const label = draft.label.trim();
    if (!code || !label) return;
    if (editing === 'new') {
      if (langs.some(l => l.code === code)) { toast('이미 있는 언어 코드입니다'); return; }
      setLangs([...langs, { code, label }]);
      toast('언어를 추가했습니다');
    } else {
      setLangs(langs.map(l => l.code === editing ? { ...l, code, label } : l));
      toast('언어를 수정했습니다');
    }
    setEditing(null);
  };

  const setDefault = (code) => setLangs(langs.map(l => ({ ...l, default: l.code === code })));
  const removeLang = (code) => {
    if (langs.length <= 1) { toast('최소 1개 언어는 필요합니다'); return; }
    const wasDefault = langs.find(l => l.code === code)?.default;
    let next = langs.filter(l => l.code !== code);
    if (wasDefault && next.length) next = next.map((l, i) => ({ ...l, default: i === 0 }));
    setLangs(next);
    toast('언어를 삭제했습니다');
  };
  const reorder = (from, to) => setLangs(move(langs, from, to));
  const bind = useDragSort(reorder);

  return (
    <div>
      <div className="toolbar">
        <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-500)', fontWeight: 500 }}>
          여기에 추가한 언어는 예제·가이드의 모든 입력 항목에 언어별 탭으로 나타납니다.
        </div>
        <button className="btn btn--primary" onClick={openNew}><Icon name="plus" size={15} stroke={2.2} />언어 추가</button>
      </div>

      <div className="panel">
        <div className="panel__head">
          <div>
            <div className="panel__title">지원 언어</div>
            <div className="panel__sub">{langs.length}개 · 드래그로 순서를 바꿀 수 있어요</div>
          </div>
        </div>
        <div className="panel__body">
          <div className="rows">
            {langs.map((l, i) => {
              const d = bind(i);
              return (
                <div key={l.code} className={"row" + (d.isDragging ? " is-dragging" : "") + (d.isOver ? " is-dragover" : "")}
                     onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
                  <span className="row__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd}><Icon name="menu" size={16} /></span>
                  <span className="lang-pip is-set" style={{ fontSize: 12, padding: '6px 9px' }}>{l.code}</span>
                  <div className="row__main">
                    <div className="row__title">{l.label}{l.default && <span className="chip" style={{ background: 'var(--brand-50)', color: 'var(--brand-700)' }}>기본 언어</span>}</div>
                  </div>
                  <div className="row__actions">
                    {!l.default && <button className="btn btn--ghost btn--sm" onClick={() => setDefault(l.code)}>기본으로</button>}
                    <button className="icon-btn" onClick={() => openEdit(l)} title="수정"><Icon name="file" size={15} /></button>
                    <button className="icon-btn icon-btn--danger" onClick={() => removeLang(l.code)} title="삭제"><Icon name="trash" size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? '언어 추가' : '언어 수정'} onClose={() => setEditing(null)}
          foot={<>
            <button className="btn btn--ghost" onClick={() => setEditing(null)}>취소</button>
            <button className="btn btn--primary" onClick={save}>저장</button>
          </>}>
          <div className="field-grid">
            <div className="field">
              <label className="field__label">언어 코드<span className="req">*</span></label>
              <input className="input mono" value={draft.code} placeholder="예: fr" maxLength={5}
                     onChange={e => setDraft({ ...draft, code: e.target.value })} />
              <div className="field__hint">ISO 코드(소문자). 예: ko, en, ja, zh, es</div>
            </div>
            <div className="field">
              <label className="field__label">표시 이름<span className="req">*</span></label>
              <input className="input" value={draft.label} placeholder="예: Français"
                     onChange={e => setDraft({ ...draft, label: e.target.value })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default LanguagesAdmin;
